import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Loader2,
  ExternalLink,
  Maximize2,
  Minimize2,
  FileText,
  Upload,
  Calendar,
  Bell,
  Building2,
  TrendingUp,
  Clock,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  ArrowLeft,
} from 'lucide-react';
import config from '../../config';
import { useAuthStore } from '../../stores/auth.store';
import contractLensService, { type Contract } from '../../services/contractlens.service';

/**
 * ContractLens surface inside the portal.
 *
 * Design notes (see docs/DESIGN-SYSTEM.md):
 *  - Metrics are typographic, not decorative. Numbers are large and quiet; only the
 *    ones that need attention carry colour, so colour keeps its meaning.
 *  - One primary action per view. Everything else is secondary or tertiary.
 *  - Cards use hairline borders and near-flat surfaces. Depth comes from spacing.
 */
type ViewMode = 'dashboard' | 'upload' | 'library' | 'fullscreen';

/** Contracts ending within this window are treated as needing attention. */
const EXPIRY_HORIZON_DAYS = 90;

export function ContractLens() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('dashboard');
  const [iframePath] = useState('/');
  const { customer } = useAuthStore();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['contractlens-stats', customer?.id],
    queryFn: () => contractLensService.getStats(),
    enabled: !!customer?.id,
    staleTime: 60000,
    retry: 1,
  });

  const { data: recentContracts, isLoading: contractsLoading } = useQuery({
    queryKey: ['contractlens-recent', customer?.id],
    queryFn: () => contractLensService.getRecentContracts(5),
    // The "expiring soon" cut-off is computed here, where the data arrives, rather than
    // during render. Rendering must stay pure, and this way every row compares against
    // the same instant instead of drifting between re-renders.
    select: (contracts) => ({
      contracts,
      expiryThreshold: new Date(Date.now() + EXPIRY_HORIZON_DAYS * 24 * 60 * 60 * 1000),
    }),
    enabled: !!customer?.id,
    staleTime: 60000,
    retry: 1,
  });

  const buildEmbedUrl = (path: string) => {
    const token = localStorage.getItem('auth_token') || '';
    return `${config.contractLensUrl}${path}?cid=${btoa(customer?.id || '')}&token=${encodeURIComponent(token)}&embedded=true`;
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const isValidOrigin = event.origin.includes('lynki') || event.origin.includes('localhost');
      if (!isValidOrigin) return;

      if (event.data.type === 'ready') {
        setIsLoading(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleIframeLoad = () => setIsLoading(false);

  const encodedCid = customer?.id ? btoa(customer.id) : '';
  const customerParam = encodedCid ? `?cid=${encodedCid}` : '';

  const openInNewTab = () => window.open(`${config.contractLensUrl}${customerParam}`, '_blank');
  const navigateToUpload = () =>
    window.open(`${config.contractLensUrl}/upload${customerParam}`, '_blank');
  const navigateToLibrary = () => window.open(`${config.contractLensUrl}${customerParam}`, '_blank');
  const navigateToNotifications = () =>
    window.open(`${config.contractLensUrl}/notifications${customerParam}`, '_blank');

  const toggleFullscreen = () =>
    setViewMode(viewMode === 'fullscreen' ? 'library' : 'fullscreen');
  const backToDashboard = () => setViewMode('dashboard');

  if (viewMode === 'dashboard') {
    return (
      <div className="h-full overflow-auto bg-ink-50">
        <div className="max-w-5xl mx-auto px-6 py-8">
          {/* Page header */}
          <div className="flex items-start justify-between gap-4 mb-8">
            <div>
              <h1 className="text-[22px] font-semibold tracking-tight text-ink-900">
                ContractLens
              </h1>
              <p className="text-[13px] text-ink-500 mt-0.5">
                AI-drevet kontraktsadministrasjon
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={openInNewTab}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium text-ink-600 bg-white border border-ink-200 rounded-[--radius-control] hover:bg-ink-50 hover:text-ink-900 transition-colors"
              >
                <ExternalLink size={14} />
                Fullversjon
              </button>
              <button
                type="button"
                onClick={navigateToUpload}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-medium text-white bg-ink-900 rounded-[--radius-control] hover:bg-ink-800 transition-colors"
              >
                <Upload size={14} />
                Last opp kontrakt
              </button>
            </div>
          </div>

          {/* Metrics. Only the attention-worthy ones carry colour. */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-ink-200 rounded-[--radius-surface] overflow-hidden border border-ink-200 mb-8">
            <StatCell
              icon={FileText}
              label="Kontrakter totalt"
              value={stats?.totalContracts ?? 0}
              onClick={navigateToLibrary}
              isLoading={statsLoading}
            />
            <StatCell
              icon={AlertTriangle}
              label="Utløper snart"
              value={stats?.expiringSoon ?? 0}
              tone="caution"
              onClick={navigateToLibrary}
              isLoading={statsLoading}
            />
            <StatCell
              icon={Bell}
              label="Varsler (30 dager)"
              value={stats?.alertsNext30Days ?? 0}
              tone="critical"
              onClick={navigateToLibrary}
              isLoading={statsLoading}
            />
            <StatCell
              icon={TrendingUp}
              label="Nylig opplastet"
              value={stats?.recentUploads ?? 0}
              onClick={navigateToLibrary}
              isLoading={statsLoading}
            />
          </div>

          <div className="grid lg:grid-cols-3 gap-5 items-start">
            {/* Quick actions */}
            <section className="lg:col-span-1 bg-white rounded-[--radius-surface] border border-ink-200 shadow-[--shadow-subtle]">
              <h2 className="px-5 pt-4 pb-3 text-[11px] font-semibold text-ink-500 uppercase tracking-[0.1em]">
                Hurtighandlinger
              </h2>
              <div className="pb-2">
                <QuickAction
                  icon={Upload}
                  label="Last opp ny kontrakt"
                  description="AI henter ut datoer og vilkår"
                  onClick={navigateToUpload}
                />
                <QuickAction
                  icon={FileText}
                  label="Bla i bibliotek"
                  description="Søk og filtrer alle kontrakter"
                  onClick={navigateToLibrary}
                />
                <QuickAction
                  icon={Calendar}
                  label="Se utløpende"
                  description={`Utløper innen ${EXPIRY_HORIZON_DAYS} dager`}
                  onClick={navigateToLibrary}
                />
                <QuickAction
                  icon={Bell}
                  label="Sjekk varsler"
                  description="Betalings- og utløpsvarsler"
                  onClick={navigateToNotifications}
                />
              </div>
            </section>

            {/* Recent contracts */}
            <section className="lg:col-span-2 bg-white rounded-[--radius-surface] border border-ink-200 shadow-[--shadow-subtle]">
              <div className="flex items-center justify-between px-5 pt-4 pb-3">
                <h2 className="text-[11px] font-semibold text-ink-500 uppercase tracking-[0.1em]">
                  Nylige kontrakter
                </h2>
                <button
                  type="button"
                  onClick={navigateToLibrary}
                  className="inline-flex items-center gap-0.5 text-[12px] font-medium text-accent-600 hover:text-accent-700 transition-colors"
                >
                  Se alle
                  <ChevronRight size={13} />
                </button>
              </div>

              {contractsLoading ? (
                <div className="flex items-center justify-center py-14">
                  <Loader2 size={20} className="animate-spin text-ink-400" />
                </div>
              ) : !recentContracts || recentContracts.contracts.length === 0 ? (
                <div className="text-center px-6 py-14">
                  <FileText size={28} className="mx-auto text-ink-300 mb-3" />
                  <p className="text-[13px] font-medium text-ink-700">Ingen kontrakter ennå</p>
                  <p className="text-[12px] text-ink-500 mt-0.5">
                    Last opp din første kontrakt for å komme i gang
                  </p>
                  <button
                    type="button"
                    onClick={navigateToUpload}
                    className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-medium text-white bg-ink-900 rounded-[--radius-control] hover:bg-ink-800 transition-colors"
                  >
                    <Upload size={14} />
                    Last opp kontrakt
                  </button>
                </div>
              ) : (
                <ul className="border-t border-ink-100">
                  {recentContracts.contracts.map((contract) => (
                    <li key={contract.id}>
                      <ContractRow
                        contract={contract}
                        onClick={navigateToLibrary}
                        expiryThreshold={recentContracts.expiryThreshold}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      </div>
    );
  }

  // Embedded view (upload, library, or fullscreen)
  return (
    <div
      className={`flex flex-col ${viewMode === 'fullscreen' ? 'fixed inset-0 z-50 bg-white' : 'h-full'}`}
    >
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-ink-200 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={backToDashboard}
            className="inline-flex items-center gap-1.5 text-[13px] text-ink-600 hover:text-ink-900 px-2 py-1.5 rounded-[--radius-control] hover:bg-ink-100 transition-colors"
          >
            <ArrowLeft size={15} />
            Oversikt
          </button>
          <span className="h-4 w-px bg-ink-200" aria-hidden />
          <h1 className="text-[13px] font-semibold text-ink-900 truncate">ContractLens</h1>
          {customer && (
            <span className="text-[12px] text-ink-500 truncate hidden sm:inline">
              {customer.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <IconButton
            label="Oppdater"
            onClick={() => {
              setIsLoading(true);
              iframeRef.current?.contentWindow?.location.reload();
            }}
          >
            <RefreshCw size={16} />
          </IconButton>
          <IconButton
            label={viewMode === 'fullscreen' ? 'Avslutt fullskjerm' : 'Fullskjerm'}
            onClick={toggleFullscreen}
          >
            {viewMode === 'fullscreen' ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </IconButton>
          <IconButton label="Åpne i ny fane" onClick={openInNewTab}>
            <ExternalLink size={16} />
          </IconButton>
        </div>
      </div>

      <div className="flex-1 relative min-h-0">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-ink-50 z-10">
            <div className="flex flex-col items-center gap-2.5">
              <Loader2 size={22} className="animate-spin text-ink-400" />
              <p className="text-[13px] text-ink-500">Laster ContractLens...</p>
            </div>
          </div>
        )}

        <iframe
          ref={iframeRef}
          src={buildEmbedUrl(iframePath)}
          onLoad={handleIframeLoad}
          className="w-full h-full border-0"
          title="ContractLens"
          allow="clipboard-read; clipboard-write"
        />
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- sub-components */

function IconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="p-2 rounded-[--radius-control] text-ink-500 hover:text-ink-900 hover:bg-ink-100 transition-colors"
    >
      {children}
    </button>
  );
}

/**
 * A single metric. Tone is reserved for values that need attention, and is only
 * applied when the value is non-zero: a calm zero should not look like an alarm.
 */
function StatCell({
  icon: Icon,
  label,
  value,
  tone,
  onClick,
  isLoading,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  tone?: 'caution' | 'critical';
  onClick: () => void;
  isLoading?: boolean;
}) {
  const active = !!tone && value > 0;
  const valueColor = !active
    ? 'text-ink-900'
    : tone === 'critical'
      ? 'text-critical'
      : 'text-caution';

  return (
    <button
      type="button"
      onClick={onClick}
      className="group bg-white p-4 text-left hover:bg-ink-50 transition-colors"
    >
      <span className="flex items-center gap-1.5 text-[11px] font-medium text-ink-500">
        <Icon size={13} className="text-ink-400" />
        <span className="truncate">{label}</span>
      </span>
      {isLoading ? (
        <span className="mt-2 block h-7 w-12 bg-ink-100 rounded animate-pulse" />
      ) : (
        <span className={`mt-1 block text-[26px] font-semibold tabular-nums leading-tight ${valueColor}`}>
          {value}
        </span>
      )}
    </button>
  );
}

function QuickAction({
  icon: Icon,
  label,
  description,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group w-full flex items-center gap-3 px-5 py-2.5 hover:bg-ink-50 transition-colors text-left"
    >
      <Icon size={16} className="text-ink-400 group-hover:text-ink-700 transition-colors shrink-0" />
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-medium text-ink-900 truncate">{label}</span>
        <span className="block text-[12px] text-ink-500 truncate">{description}</span>
      </span>
      <ChevronRight
        size={15}
        className="text-ink-300 group-hover:text-ink-500 transition-colors shrink-0"
      />
    </button>
  );
}

function ContractRow({
  contract,
  onClick,
  expiryThreshold,
}: {
  contract: Contract;
  onClick: () => void;
  /**
   * Precomputed cut-off date. Passed in rather than derived from Date.now() here, so
   * rendering stays pure and every row in a list judges "expiring" against the same
   * instant instead of drifting between renders.
   */
  expiryThreshold: Date;
}) {
  const isExpiring =
    contract.status === 'Expiring' ||
    (!!contract.endDate && new Date(contract.endDate) <= expiryThreshold);

  const statusLabel =
    contract.status === 'Active'
      ? 'Aktiv'
      : contract.status === 'Expired'
        ? 'Utløpt'
        : contract.status === 'Expiring'
          ? 'Utløper'
          : contract.status;

  const statusClass = isExpiring
    ? 'bg-caution/10 text-caution'
    : contract.status === 'Active'
      ? 'bg-positive/10 text-positive'
      : 'bg-ink-100 text-ink-600';

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-5 py-3 hover:bg-ink-50 transition-colors text-left border-b border-ink-100 last:border-b-0"
    >
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-medium text-ink-900 truncate">
          {contract.title}
        </span>
        <span className="flex items-center gap-1 mt-0.5 text-[12px] text-ink-500">
          <Building2 size={11} className="text-ink-400 shrink-0" />
          <span className="truncate">{contract.companyName}</span>
        </span>
      </span>

      <span className="flex flex-col items-end gap-1 shrink-0">
        <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${statusClass}`}>
          {statusLabel}
        </span>
        {contract.endDate && (
          <span className="flex items-center gap-1 text-[11px] text-ink-400 tabular-nums">
            <Clock size={10} />
            {new Date(contract.endDate).toLocaleDateString('nb-NO')}
          </span>
        )}
      </span>
    </button>
  );
}

export default ContractLens;
